import React, { useState, useEffect, useRef } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { Loader2, Upload, X } from "lucide-react";

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

const QuickAnswerSchema = Yup.object().shape({
	shortcut: Yup.string()
		.min(2, "Muito curto")
		.max(15, "Muito longo")
		.required("Campo obrigatório"),
	message: Yup.string()
		.min(4, "Muito curto")
		.max(30000, "Muito longo")
		.required("Campo obrigatório"),
});

const gerarShortcut = (mensagem) => {
	const base = (mensagem || "")
		.toLowerCase()
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.replace(/[^a-z0-9 ]/g, " ")
		.trim()
		.split(/\s+/)
		.slice(0, 3)
		.join("-")
		.slice(0, 15)
		.replace(/^-|-$/g, "");
	return base.length >= 2 ? base : "resp";
};

// Modal de CADASTRO/EDIÇÃO de respostas rápidas.
// Diferente de QuickAnswersModal (que é "usar no chat"), este persiste no banco.
// Regras de escopo (administrador vs usuário comum):
//   - Administrador: vê toggle "Disponível para todos os usuários".
//       On  → global (todos da empresa veem).
//       Off → resposta privada do próprio admin.
//   - Usuário comum: não vê o toggle. Tudo o que ele cadastra fica privado
//     (apenas para ele). O backend rejeita qualquer tentativa de "forçar"
//     global por usuário comum.
const QuickAnswersEditorModal = ({
	open,
	onClose,
	quickAnswerId = null,
	user,
	onSaved,
}) => {
	const isMounted = useRef(true);
	const ehAdmin = user && String(user.profile || "").toLowerCase() === "admin";
	const arquivoInputRef = useRef(null);

	const initialState = {
		shortcut: "",
		message: "",
		global: true,
		mediaUrl: null,
		mediaType: null,
		mediaName: null,
	};

	const [quickAnswer, setQuickAnswer] = useState(initialState);
	const [arquivo, setArquivo] = useState(null);
	const [carregandoDados, setCarregandoDados] = useState(false);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		if (!open) return;

		// Abrindo novo
		if (!quickAnswerId) {
			setQuickAnswer({
				...initialState,
				// Admin por padrão cria global; ele pode desligar se quiser.
				global: ehAdmin ? true : false,
			});
			setArquivo(null);
			return;
		}

		// Abrindo para editar
		setCarregandoDados(true);
		api.get(`/quickAnswers/${quickAnswerId}`)
			.then((resp) => {
				if (!isMounted.current) return;
				const dados = resp.data || {};
				setQuickAnswer({
					shortcut: dados.shortcut || "",
					message: dados.message || "",
					// NULL no banco = global.
					global: dados.userId == null,
					mediaUrl: dados.mediaUrl || null,
					mediaType: dados.mediaType || null,
					mediaName: dados.mediaName || null,
				});
				setArquivo(null);
			})
			.catch((err) => toastError(err))
			.finally(() => isMounted.current && setCarregandoDados(false));
	}, [open, quickAnswerId, ehAdmin]);

	const handleClose = () => {
		setQuickAnswer(initialState);
		setArquivo(null);
		onClose && onClose();
	};

	const montarFormData = (values) => {
		const fd = new FormData();
		fd.append("shortcut", values.shortcut.trim());
		fd.append("message", values.message || "");
		// Só admin pode decidir global.
		if (ehAdmin) {
			fd.append("global", values.global ? "true" : "false");
		}
		if (arquivo) {
			fd.append("medias", arquivo, arquivo.name);
		}
		if (!arquivo && quickAnswerId && !quickAnswer.mediaUrl) {
			// sem arquivo, sem mudança
		}
		// Se o usuário clicou em "tirar anexo" (ainda não temos UI para isso),
		// mandaria removerMidia=true. Por enquanto não precisa.
		return fd;
	};

	const submit = async (values, actions) => {
		try {
			// Garante atalho mínimo
			const shortcutFinal =
				values.shortcut && values.shortcut.trim().length >= 2
					? values.shortcut.trim()
					: gerarShortcut(values.message);

			const payload = { ...values, shortcut: shortcutFinal };
			let resp;

			if (quickAnswerId) {
				resp = await api.put(
					`/quickAnswers/${quickAnswerId}`,
					montarFormData(payload),
					{ headers: { "Content-Type": "multipart/form-data" } }
				);
				toast.success("Resposta rápida atualizada.");
			} else {
				resp = await api.post(
					"/quickAnswers",
					montarFormData(payload),
					{ headers: { "Content-Type": "multipart/form-data" } }
				);
				toast.success("Resposta rápida cadastrada.");
			}

			actions.setSubmitting(false);
			onSaved && onSaved(resp.data);
			handleClose();
		} catch (err) {
			actions.setSubmitting(false);
			toastError(err);
		}
	};

	const removerAnexo = () => {
		setArquivo(null);
		setQuickAnswer((prev) => ({
			...prev,
			mediaUrl: null,
			mediaType: null,
			mediaName: null,
		}));
		// Vai precisar mandar removerMidia=true no update
	};

	return (
		<Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
			<DialogContent className="sm:max-w-xl">
				<DialogHeader>
					<DialogTitle>
						{quickAnswerId ? "Editar resposta rápida" : "Nova resposta rápida"}
					</DialogTitle>
					<DialogDescription>
						Atalhos que a equipe pode usar digitando <span className="font-medium">/nome-do-atalho</span>{" "}
						dentro do atendimento.
					</DialogDescription>
				</DialogHeader>

				{carregandoDados ? (
					<div className="py-10 text-center text-sm text-muted-foreground">
						<Loader2 className="inline h-5 w-5 animate-spin mr-2" />
						Carregando...
					</div>
				) : (
					<Formik
						initialValues={quickAnswer}
						enableReinitialize={true}
						validationSchema={QuickAnswerSchema}
						onSubmit={submit}
					>
						{({ errors, touched, isSubmitting, setFieldValue, values }) => (
							<Form className="space-y-4">
								<div className="grid grid-cols-1 gap-4 sm:grid-cols-5">
									<div className="space-y-1.5 sm:col-span-2">
										<Label htmlFor="shortcut">Atalho</Label>
										<Field
											as={Input}
											id="shortcut"
											name="shortcut"
											placeholder="ex.: horario"
											onChange={(e) => {
												const v = e?.target?.value ?? "";
												setFieldValue(
													"shortcut",
													String(v)
														.toLowerCase()
														.normalize("NFD")
														.replace(/[\u0300-\u036f]/g, "")
														.replace(/[^a-z0-9-]/g, "-")
														.slice(0, 15)
												);
											}}
										/>
										{touched.shortcut && errors.shortcut && (
											<p className="text-xs text-destructive">{errors.shortcut}</p>
										)}
									</div>
									<div className="sm:col-span-3" />
								</div>

								<div className="space-y-1.5">
									<Label htmlFor="message">Mensagem</Label>
									<Field name="message">
										{({ field }) => (
											<Textarea
												{...field}
												id="message"
												rows={8}
												placeholder="Ex.: Atendemos de segunda a sexta, das 8h às 18h."
												onChange={(e) => {
													field.onChange(e);
													// Autocompleta atalho quando vazio ou igual ao gerado
													try {
														const atual = (values?.shortcut || "").trim();
														if (!atual || atual.length < 2) {
															const curta = gerarShortcut(e.target?.value || "");
															setFieldValue("shortcut", curta, false);
														}
													} catch (_) {}
												}}
											/>
										)}
									</Field>
									{touched.message && errors.message && (
										<p className="text-xs text-destructive">{errors.message}</p>
									)}
								</div>

								{/* Mídia opcional */}
								<div className="space-y-2">
									<Label>Anexo (opcional)</Label>
									<div className="flex flex-wrap items-center gap-3">
										<input
											ref={arquivoInputRef}
											type="file"
											className="hidden"
											onChange={(e) => {
												const f = e?.target?.files?.[0] || null;
												setArquivo(f);
												if (f) {
													setQuickAnswer((prev) => ({
														...prev,
														mediaName: f.name,
													}));
												}
											}}
										/>
										<Button
											type="button"
											variant="outline"
											onClick={() => arquivoInputRef.current?.click()}
										>
											<Upload className="mr-2 h-4 w-4" />
											Escolher arquivo
										</Button>
										{(arquivo || quickAnswer.mediaName) && (
											<div className="flex items-center gap-2 rounded border bg-muted/40 px-3 py-1.5 text-sm">
												<span className="truncate max-w-[220px]">
													{arquivo?.name || quickAnswer.mediaName}
												</span>
												<Button
													type="button"
													variant="ghost"
													size="icon"
													className="h-6 w-6 text-destructive"
													onClick={removerAnexo}
													aria-label="Remover anexo"
												>
													<X className="h-3.5 w-3.5" />
												</Button>
											</div>
										)}
									</div>
									<p className="text-xs leading-relaxed text-muted-foreground">
										Imagem, PDF ou áudio para anexar junto com a mensagem.
									</p>
								</div>

								{/* Escopo: só admin vê */}
								{ehAdmin && (
									<div className="flex items-center justify-between gap-4 rounded-lg border bg-muted/30 px-4 py-3">
										<div>
											<p className="text-sm font-medium">
												Disponível para todos os usuários
											</p>
											<p className="text-xs leading-relaxed text-muted-foreground">
												Ligado: toda a equipe vê esta resposta. Desligado: só você a verá.
											</p>
										</div>
										<Switch
											checked={!!values.global}
											onCheckedChange={(v) => setFieldValue("global", v)}
										/>
									</div>
								)}

								{!ehAdmin && (
									<div className="rounded-lg border bg-muted/20 px-4 py-2.5 text-xs leading-relaxed text-muted-foreground">
										Esta resposta ficará disponível apenas para o seu usuário.
										Administradores podem criar respostas globais para toda a equipe.
									</div>
								)}

								<DialogFooter className="flex-col sm:flex-row sm:justify-end sm:space-x-2">
									<Button
										type="button"
										variant="outline"
										onClick={handleClose}
										disabled={isSubmitting}
										className="sm:w-auto w-full"
									>
										Cancelar
									</Button>
									<Button
										type="submit"
										variant="default"
										disabled={isSubmitting}
										className="sm:w-auto w-full"
									>
										{isSubmitting && (
											<Loader2 className="mr-2 h-4 w-4 animate-spin" />
										)}
										{quickAnswerId ? "Salvar alterações" : "Cadastrar resposta"}
									</Button>
								</DialogFooter>
							</Form>
						)}
					</Formik>
				)}
			</DialogContent>
		</Dialog>
	);
};

export default QuickAnswersEditorModal;
